import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { VenueDetailsService } from '../venue-details.service';
import { UserService } from 'app/Shared/Services/user.service';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { map, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DrawerModule } from 'primeng/drawer';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { CommonValidators } from 'app/Shared/Constant/common-validators';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { format } from 'date-fns';
import { SharedHttpClientService } from 'app/Shared/Services/shared-http-client.service';
import { UrlsService } from 'app/Shared/Services/urls.service';
import { APIURL } from 'app/Shared/Constant/API_Urls';

export interface VenueDetails {
  id?: number;
  venueMPID?: string;
  venueName?: string;
  micCode?:String;
  venueFirmName?: string;
  isApproved?: boolean;
  venueType?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-add-edit-view-venue-details',
  imports: [DialogModule, CommonModule, ButtonModule, FormsModule, Select,
        MatProgressSpinnerModule, MatTooltipModule, ConfirmDialogModule, DrawerModule, ToastModule, ReactiveFormsModule, ToggleSwitchModule],
  templateUrl: './add-edit-view-venue-details.component.html',
  styleUrl: './add-edit-view-venue-details.component.scss'
})
export class AddEditViewVenueDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input('columns') columns: any;
  @Input() showDialog: boolean = false;
  @Input() rowData: VenueDetails = {};
  @Input() mode: any = 'add';
  
  @Output() visibilityChange = new EventEmitter<boolean>();
  
  venueDetailsForm!: FormGroup;
  formSubmitted = false;
  header: any = "Add Venue Details";

  venueMPID: string = '';
  venueName: string = '';
  venueType: string = '';
  isApproved: boolean = false;
  exchangeList:  any [] = [];
  private destroy$ = new Subject<void>();
  isVenueDetailsLoadingResults: boolean = false;

  constructor(
    public venueDetailsService: VenueDetailsService,
    private userService: UserService,
    private messageService: MessageService,
    private sharedHttpClientService: SharedHttpClientService,
    private readonly urlService: UrlsService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['showDialog'] && changes['showDialog'].currentValue === true) {
        this.buildForm();
        this.formSubmitted = false;
        this.getExchangeList();
        if(this.mode === 'add') {
          this.header = "Add Venue Details";
        } else if(this.mode === 'edit') {
          this.header = "Edit Venue Details";
        } else {
          this.header = "Venue Details";
        }
      }
  }

  buildForm() {
    const formControls: any = {};
    
    if (!this.columns) {
      return;
    }
    
    this.columns.forEach((col: any) => {
      if (col.ui_columnname !== 'Action') {
        const validators = [];
        
        if (col.isrequired === 'true') {
          validators.push(CommonValidators.requiredTrim());
        }

        const isVenueNameDisabled = col.mappingkey === 'venueName' && 
                            (this.mode === 'view' || this.mode === 'edit');

        const controlConfig: any = {
          value: this.rowData[col.mappingkey as keyof VenueDetails] || '',
          disabled: this.mode === 'view' || col.disabled === true || isVenueNameDisabled
        };
        
        formControls[col.mappingkey] = [controlConfig, validators];
      }
    });
    
    this.venueDetailsForm = this.fb.group(formControls);

    this.isApproved = this.rowData['isApproved'] ? true : false;
    this.venueDetailsForm.get('isApproved')?.setValue(this.isApproved);
    this.venueDetailsForm.get('venueType')?.setValue('EV/SRV');

    this.venueDetailsForm.get('venueMPID')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.onVenueMPIDChange(value);
      });
  }

  onVenueMPIDChange(value: string) {
    if(Boolean(this.exchangeList)) {
      const filteredObject = this.exchangeList.filter(obj => obj.exchange === value);
      if(Boolean(filteredObject) && filteredObject.length !== 0) {
        this.venueDetailsForm.get('venueType')?.setValue('PRV');
      } else {
        this.venueDetailsForm.get('venueType')?.setValue('EV/SRV');
      }
    }
  }

  onSave() {
    this.formSubmitted = true;

    Object.keys(this.venueDetailsForm.controls).forEach(key => {
      const control = this.venueDetailsForm.get(key);
      if (control && typeof control.value === 'string') {
        control.setValue(control.value.trim(), { emitEvent: false });
      }
    });
    
    this.venueDetailsForm.updateValueAndValidity();

    
    if(this.venueDetailsForm.valid) {
      this.isVenueDetailsLoadingResults = true;
      let approvedBy = this.venueDetailsForm.getRawValue()['approvedBy'];
      let approvedDate = this.venueDetailsForm.getRawValue()['approvedDate'];

      const currentIsApprovedValue = Boolean(this.venueDetailsForm.value['isApproved']);
      const formattedDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");

  
      if(!this.isApproved && currentIsApprovedValue) {
        approvedBy = this.userService.getUserName();
        approvedDate = formattedDate;
      } else if(this.isApproved && !currentIsApprovedValue) {
        approvedBy = null;
        approvedDate = null;
      }

      let payload = {
        "id" : this.rowData.id,
        "firmMPID": this.userService.getActiveMPID(),
        "venueMPID": this.venueDetailsForm.value['venueMPID'],
        "venueName" : this.venueDetailsForm.getRawValue()['venueName'],
        "micCode": this.venueDetailsForm.value['micCode'],
        "venueFirmName": this.venueDetailsForm.value['venueFirmName'],
        "isApproved": currentIsApprovedValue,
        "approvedBy": approvedBy,
        "approvedDate": approvedDate,
        "venueType": this.venueDetailsForm.value['venueType'],
        "user": this.userService.getUserName(),
      }

      if(this.mode === 'add') {
        /* Catch User Action Log */
        this.userService.logUserActivity("Add - ODR Venue Details", null, {
          'Text1': this.userService.userFirstName + ' Added ODR Venue Details',
          'Text2': 'User Filter Parameters: ' + JSON.stringify(payload)
        });
        this.venueDetailsService.getAdd(payload)
          .pipe(map((response: Response) => {
            return response;
          }))
          .subscribe({
            next: (data:any) => {
              this.isVenueDetailsLoadingResults = false;
                  if (data.status) {
                    this.venueDetailsService.reloadData();
                    this.messageService.add({ 
                      severity: 'success', 
                      summary: 'Success', 
                      detail: 'Record saved successfully' 
                    });
                    this.onClose();
                  } else {
                    this.messageService.add({ 
                      severity: 'error', 
                      summary: 'Error', 
                      detail: data.data 
                    });
                  }
            },
            error: (err: HttpErrorResponse) => {
              this.isVenueDetailsLoadingResults = false;
              this.venueDetailsService.isAdd = false;
            }
          });  
      } else if(this.mode === 'edit') {
        /* Catch User Action Log */
        this.userService.logUserActivity("Edit - ODR Venue Details", null, {
          'Text1': this.userService.userFirstName + ' Edited ODR Venue Details',
          'Text2': 'User Filter Parameters: ' + JSON.stringify(payload)
        });
        this.venueDetailsService.getEdit(payload)
          .pipe(map((response: Response) => {
            return response;
          }))
          .subscribe({
            next: (data: any) => {
              this.isVenueDetailsLoadingResults = false;
              if (data.status) {
                this.venueDetailsService.reloadData();
                this.messageService.add({ 
                  severity: 'success', 
                  summary: 'Success', 
                  detail: 'Record updated successfully' 
                });
                this.onClose();
              } else {
                this.messageService.add({ 
                  severity: 'error', 
                  summary: 'Error', 
                  detail: data.data 
                });
              }
            },
            error: (err: HttpErrorResponse) => {
              this.isVenueDetailsLoadingResults = false;
              this.messageService.add({ 
                severity: 'error', 
                summary: 'Error', 
                detail: err.message 
              });
            }
          });
      }

    } else {
      Object.keys(this.venueDetailsForm.controls).forEach(key => {
        this.venueDetailsForm.controls[key].markAsTouched();
      });
      
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Validation Error', 
        detail: 'Please fill all required fields correctly' 
      });
    }
  }

  onClose() {
    this.formSubmitted = false;
    this.visibilityChange.emit(false);
    if (this.venueDetailsForm) {
      this.venueDetailsForm.reset();
    }
  }

  hasError(fieldName: string, errorType: string): boolean {
    const control = this.venueDetailsForm.get(fieldName);
    return !!(control && control.hasError(errorType) && (control.touched || this.formSubmitted));
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.venueDetailsForm.get(fieldName);
    return !!(control && control.invalid && (control.touched || this.formSubmitted));
  }

  getErrorMessage(col: any): string {
    const control = this.venueDetailsForm.get(col.mappingkey);
    
    if (!control || !control.errors || (!control.touched && !this.formSubmitted)) {
      return '';
    }

    if (control.errors['required']) {
      return `${col.ui_columnname} is required`;
    }
    
    if (control.errors['whitespace']) {
      return `${col.ui_columnname} cannot be empty or whitespace only`;
    }
    
    if (control.errors['notNumeric']) {
      return `${col.ui_columnname} invalid input`;
    }
    
    return 'Invalid value';
  }

  getExchangeList(){

    this.sharedHttpClientService.get(
      this.urlService.setAPIURl(
        APIURL.ODR_ExchangeList))
      .subscribe(result => {
        this.exchangeList = result.Data;
      });
    
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();    
  }

}
